import React, { useState, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { Lightbox } from './Lightbox';

const featureImage1Def: SlotGroupDefinition = {
  label: 'Feature Image 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const featureImage2Def: SlotGroupDefinition = {
  label: 'Feature Image 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const featureImage3Def: SlotGroupDefinition = {
  label: 'Feature Image 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[6].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[6].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 6,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const featureImage4Def: SlotGroupDefinition = {
  label: 'Feature Image 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[9].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[9].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 9,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

export function PropertyOverview() {
  const preTitle = useContentToken('propertyoverview.pre_title');
  const title = useContentToken('propertyoverview.title');
  const image1 = useSlotGroup('propertyoverview.feature_image_1', featureImage1Def);
  const image2 = useSlotGroup('propertyoverview.feature_image_2', featureImage2Def);
  const image3 = useSlotGroup('propertyoverview.feature_image_3', featureImage3Def);
  const image4 = useSlotGroup('propertyoverview.feature_image_4', featureImage4Def);

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const lightboxImages = useMemo(
    () => [
      { url: image1.image || '', alt: image1.alt || '' },
      { url: image2.image || '', alt: image2.alt || '' },
      { url: image3.image || '', alt: image3.alt || '' },
      { url: image4.image || '', alt: image4.alt || '' },
    ],
    [image1.image, image1.alt, image2.image, image2.alt, image3.image, image3.alt, image4.image, image4.alt],
  );

  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useStaggerReveal<HTMLDivElement>();

  return (
    <section className="py-16 md:py-24 bg-[var(--vibe-color-background)]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div ref={headingRef} className="reveal-fade-up">
          <EditableToken
            path="propertyoverview.pre_title"
            defaultValue="Life at our community is like nowhere else"
            as="p"
            className="font-body text-sm sm:text-base text-[var(--vibe-color-text)] mb-6 tracking-wide"
          >
            {preTitle}
          </EditableToken>

          <EditableToken
            path="propertyoverview.title"
            defaultField="property_name"
            defaultValue="Our Community"
            as="h2"
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold overview-title-gradient mb-4 leading-tight"
          >
            {title}
          </EditableToken>

          <div className="w-20 h-1 mx-auto mb-8 rounded-full overview-accent-bar" />

          <RichTextContent
            path="propertyoverview.description"
            as="div"
            className="font-body text-sm sm:text-base text-[var(--vibe-color-text)] max-w-3xl mx-auto mb-14 leading-relaxed [&_a]:text-primary [&_a]:underline"
          />
        </div>

        <div ref={gridRef} className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <EditableSlotGroup groupPath="propertyoverview.feature_image_1" definition={featureImage1Def}>
            <button
              data-reveal
              className="reveal-scale aspect-[4/3] overflow-hidden group cursor-zoom-in w-full relative"
              onClick={() => setLightboxIndex(0)}
              aria-label="Enlarge image 1"
            >
              <VibesitesImage
                url={image1.image || ''}
                alt={image1.alt || ''}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                sizes="(min-width: 768px) 240px, 50vw"
              />
              <span className="overview-zoom-icon">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0Zm-6-3v6m-3-3h6" />
                </svg>
              </span>
            </button>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="propertyoverview.feature_image_2" definition={featureImage2Def}>
            <button
              data-reveal
              className="reveal-scale aspect-[4/3] overflow-hidden group cursor-zoom-in w-full relative"
              onClick={() => setLightboxIndex(1)}
              aria-label="Enlarge image 2"
            >
              <VibesitesImage
                url={image2.image || ''}
                alt={image2.alt || ''}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                sizes="(min-width: 768px) 240px, 50vw"
              />
              <span className="overview-zoom-icon">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0Zm-6-3v6m-3-3h6" />
                </svg>
              </span>
            </button>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="propertyoverview.feature_image_3" definition={featureImage3Def}>
            <button
              data-reveal
              className="reveal-scale aspect-[4/3] overflow-hidden group cursor-zoom-in w-full relative"
              onClick={() => setLightboxIndex(2)}
              aria-label="Enlarge image 3"
            >
              <VibesitesImage
                url={image3.image || ''}
                alt={image3.alt || ''}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                sizes="(min-width: 768px) 240px, 50vw"
              />
              <span className="overview-zoom-icon">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0Zm-6-3v6m-3-3h6" />
                </svg>
              </span>
            </button>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="propertyoverview.feature_image_4" definition={featureImage4Def}>
            <button
              data-reveal
              className="reveal-scale aspect-[4/3] overflow-hidden group cursor-zoom-in w-full relative"
              onClick={() => setLightboxIndex(3)}
              aria-label="Enlarge image 4"
            >
              <VibesitesImage
                url={image4.image || ''}
                alt={image4.alt || ''}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                sizes="(min-width: 768px) 240px, 50vw"
              />
              <span className="overview-zoom-icon">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0Zm-6-3v6m-3-3h6" />
                </svg>
              </span>
            </button>
          </EditableSlotGroup>
        </div>
      </div>

      <Lightbox
        images={lightboxImages}
        selectedIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onSelect={setLightboxIndex}
      />

      <style>{`
        .overview-title-gradient {
          background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .overview-accent-bar {
          background: linear-gradient(90deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 75%, black) 100%);
        }
        .overview-zoom-icon {
          position: absolute;
          bottom: 0.5rem;
          right: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 2rem;
          height: 2rem;
          border-radius: 9999px;
          background: rgba(0, 0, 0, 0.45);
          color: #fff;
          opacity: 0;
          transition: opacity 0.2s ease;
          pointer-events: none;
        }
        button:hover .overview-zoom-icon,
        button:focus-visible .overview-zoom-icon {
          opacity: 1;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.pre_title': {
      label: 'Subtitle',
      type: 'text',
      defaultValue: 'Life at our community is like nowhere else',
    },
    'propertyoverview.title': {
      label: 'Title',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Our Community',
    },
    'propertyoverview.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_long_description',
      defaultValue: '<p>Discover the perfect blend of comfort, style, and convenience at our community.</p>',
    },
  },
  slotGroups: {
    'propertyoverview.feature_image_1': featureImage1Def,
    'propertyoverview.feature_image_2': featureImage2Def,
    'propertyoverview.feature_image_3': featureImage3Def,
    'propertyoverview.feature_image_4': featureImage4Def,
  },
};

(PropertyOverview as any).contentSchema = contentSchema;

export default PropertyOverview;
